import { useQueryState } from 'nuqs';
import { useEffect } from 'react';

import { useContentEditorStore } from './useContentEditorStore';

/**
 * Two-way sync between the store's selected block and the `?block=` URL
 * param. Restores selection on refresh / shared links (call it AFTER the
 * effect that calls openPage, so restore runs against the opened page), and
 * mirrors later selection changes into the URL without history spam.
 */
export function useSelectionUrlSync() {
  const [blockParam, setBlockParam] = useQueryState('block');
  const activeKey = useContentEditorStore((s) => s.activeKey);
  const selectedId = useContentEditorStore((s) => s.selectedId);
  const selectBlock = useContentEditorStore((s) => s.selectBlock);

  // URL → store: initial restore, back/forward navigation, page switches.
  // selectBlock validates the id against the draft, so a stale URL simply
  // clears the selection.
  useEffect(() => {
    if (!activeKey) return;
    const target = blockParam ?? undefined;
    if (target !== useContentEditorStore.getState().selectedId) {
      selectBlock(target);
    }
  }, [blockParam, activeKey, selectBlock]);

  // Store → URL: mirror the selection into the param. Reads the CURRENT
  // selection via getState() rather than the render-time closure: both sync
  // effects run in the same flush after openPage sets activeKey, so the
  // closure's selectedId is stale relative to the restore the effect above
  // just performed — comparing against it would clear the param and undo the
  // restore (selected-then-immediately-unselected on refresh).
  useEffect(() => {
    if (!activeKey) return;
    const current = useContentEditorStore.getState().selectedId;
    if ((blockParam ?? undefined) !== current) {
      void setBlockParam(current ?? null, { history: 'replace' });
    }
    // blockParam deliberately omitted: this effect only pushes store → URL;
    // reacting to blockParam here would ping-pong with the effect above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId, activeKey, setBlockParam]);
}

/** Ctrl/Cmd+Z undo, Ctrl/Cmd+Shift+Z / Ctrl+Y redo, outside text fields. */
export function useEditorKeyboardShortcuts() {
  const undo = useContentEditorStore((s) => s.undo);
  const redo = useContentEditorStore((s) => s.redo);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (!(e.ctrlKey || e.metaKey)) return;
      const target = e.target as HTMLElement | null;
      // Text fields keep their native undo behavior.
      if (
        target &&
        (target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.isContentEditable)
      ) {
        return;
      }
      const key = e.key.toLowerCase();
      if (key === 'z') {
        e.preventDefault();
        if (e.shiftKey) redo();
        else undo();
      } else if (key === 'y') {
        e.preventDefault();
        redo();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [undo, redo]);
}

export const useCanUndo = () =>
  useContentEditorStore((s) => {
    const h = s.activeKey ? s.history[s.activeKey] : undefined;
    return Boolean(h && h.past.length > 0);
  });

export const useCanRedo = () =>
  useContentEditorStore((s) => {
    const h = s.activeKey ? s.history[s.activeKey] : undefined;
    return Boolean(h && h.future.length > 0);
  });
